import { useEffect, useState } from "react";

import {
  Autocomplete,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import { useDispatch } from "react-redux";
import { ThunkDispatch } from "@reduxjs/toolkit";
import { RootState } from "../../store/Store";
import {
  FetchDataSuccessAction,
  FetchDataFailureAction,
  fetchData,
  postData,
  putData,
  deleteData,
} from "../../store/actions";
import { ToastVariant, useCustomToast } from "../../components/Methods/SnackBar";

import {
  getAllDesignations,
  skillSections,
  designationSections,
  designationSectionDetail,
} from "../../Service/Apis";

// ── Types ─────────────────────────────────────────────────────────────────

interface Designation {
  id: string;
  designation_name: string;
}

interface SkillSection {
  section_id: string;
  section_name: string;
}

interface DesignationSectionRow {
  id: string;
  designation: string;
  designation_name: string;
  section: string;
  section_name: string;
  weightage: number;
}

// ── Component ──────────────────────────────────────────────────────────────

const DesignationSectionAssignment = () => {
  const [designations, setDesignations] = useState<Designation[]>([]);
  const [sections, setSections] = useState<SkillSection[]>([]);
  const [selectedDesignation, setSelectedDesignation] = useState<Designation | null>(null);
  const [assignments, setAssignments] = useState<DesignationSectionRow[]>([]);

  // Manage sections modal
  const [addSectionOpen, setAddSectionOpen] = useState(false);
  const [newSections, setNewSections] = useState<SkillSection[]>([]);
  const [newWeightages, setNewWeightages] = useState<Record<string, string>>({});
  const [modalExistingWeightages, setModalExistingWeightages] = useState<Record<string, string>>({});
  const [addLoading, setAddLoading] = useState(false);

  // Delete assignment
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [loadingAssignments, setLoadingAssignments] = useState(false);

  const showToast = useCustomToast();
  const dispatch = useDispatch();
  const dispatch2: ThunkDispatch<RootState, null, FetchDataSuccessAction | FetchDataFailureAction> =
    useDispatch();

  const toast = (msg: string, variant: ToastVariant) => showToast(msg, variant, true);

  // Clamp a string value to at most 2 decimal places, used by weightage inputs.
  const clampTo2Dec = (val: string): string => {
    if (!val) return val;
    const dotIndex = val.indexOf(".");
    if (dotIndex === -1) return val;
    return val.slice(0, dotIndex + 3);
  };

  // total weightage of already-saved assignments (for main view badge)
  const totalWeightage = assignments.reduce((sum, a) => sum + Number(a.weightage), 0);
  const isValid = totalWeightage === 100;

  // modal computed values — all rounded to 2 dp to avoid floating-point drift
  const modalExistingTotal = parseFloat(
    assignments
      .reduce((sum, row) => sum + (parseFloat(modalExistingWeightages[row.id] || "0") || 0), 0)
      .toFixed(2)
  );
  const modalNewTotal = parseFloat(
    newSections
      .reduce((sum, s) => sum + (parseFloat(newWeightages[s.section_id] || "0") || 0), 0)
      .toFixed(2)
  );
  const modalTotal = parseFloat((modalExistingTotal + modalNewTotal).toFixed(2));
  const modalValid = modalTotal === 100;
  const remainingBudget = parseFloat((100 - modalExistingTotal).toFixed(2));

  // ── Fetch designations and sections on mount ──────────────────────────

  useEffect(() => {
    const load = async () => {
      try {
        const [desRes, secRes]: any = await Promise.all([
          dispatch2(fetchData(getAllDesignations)),
          dispatch2(fetchData(skillSections)),
        ]);
        setDesignations(desRes as Designation[]);
        setSections(secRes as SkillSection[]);
      } catch {
        toast("Failed to load data.", "error");
      }
    };
    load();
  }, []);

  // ── Fetch assignments when designation changes ────────────────────────

  const fetchAssignments = async (designationId: string) => {
    setLoadingAssignments(true);
    try {
      const res: any = await dispatch2(
        fetchData(`${designationSections}?designation_id=${designationId}`)
      );
      setAssignments(res as DesignationSectionRow[]);
    } catch {
      toast("Failed to load section assignments.", "error");
    } finally {
      setLoadingAssignments(false);
    }
  };

  const handleDesignationChange = (des: Designation | null) => {
    setSelectedDesignation(des);
    setAssignments([]);
    setAddSectionOpen(false);
    setNewSections([]);
    setNewWeightages({});
    setModalExistingWeightages({});
    if (des) fetchAssignments(des.id);
  };

  // ── Open / Close Modal ────────────────────────────────────────────────

  const handleOpenModal = () => {
    const init: Record<string, string> = {};
    assignments.forEach((row) => { init[row.id] = String(row.weightage); });
    setModalExistingWeightages(init);
    setNewSections([]);
    setNewWeightages({});
    setAddSectionOpen(true);
  };

  const handleCloseModal = () => {
    setAddSectionOpen(false);
    setNewSections([]);
    setNewWeightages({});
    setModalExistingWeightages({});
  };

  // ── Split new sections equally across remaining budget ────────────────

  const handleSplitEqually = () => {
    if (newSections.length === 0 || remainingBudget <= 0) return;
    const perSection = parseFloat((remainingBudget / newSections.length).toFixed(2));
    const equal: Record<string, string> = {};
    newSections.forEach((s) => { equal[s.section_id] = String(perSection); });
    setNewWeightages(equal);
  };

  // ── Save All (PUT changed existing + POST new) ────────────────────────

  const handleSaveAll = async () => {
    if (!selectedDesignation) return;

    for (const row of assignments) {
      const w = parseFloat(modalExistingWeightages[row.id] || "");
      if (isNaN(w) || w <= 0 || w > 100) {
        toast(`Enter a valid weightage for "${row.section_name}".`, "error");
        return;
      }
    }
    for (const sec of newSections) {
      const w = parseFloat(newWeightages[sec.section_id] || "");
      if (isNaN(w) || w <= 0 || w > 100) {
        toast(`Enter a valid weightage for "${sec.section_name}".`, "error");
        return;
      }
    }
    if (!modalValid) {
      toast(`Total weightage must equal 100%. Currently ${modalTotal.toFixed(2)}%.`, "error");
      return;
    }

    const changedExisting = assignments.filter(
      (row) => String(row.weightage) !== modalExistingWeightages[row.id]
    );

    if (changedExisting.length === 0 && newSections.length === 0) {
      handleCloseModal();
      return;
    }

    setAddLoading(true);
    try {
      // Phase 1: update existing rows first so backend total is reduced before adding new rows.
      if (changedExisting.length > 0) {
        await Promise.all(
          changedExisting.map((row) =>
            dispatch2(putData(designationSectionDetail(row.id), {
              weightage: parseFloat(parseFloat(modalExistingWeightages[row.id]).toFixed(2)),
            }))
          )
        );
      }

      // Phase 2: create new rows after updates have committed.
      if (newSections.length > 0) {
        await Promise.all(
          newSections.map((sec) =>
            dispatch2(postData(designationSections, {
              designation: selectedDesignation.id,
              section: sec.section_id,
              weightage: parseFloat(parseFloat(newWeightages[sec.section_id]).toFixed(2)),
            }))
          )
        );
      }
      const msgs: string[] = [];
      if (changedExisting.length > 0) msgs.push(`${changedExisting.length} weightage(s) updated`);
      if (newSections.length > 0) msgs.push(`${newSections.length} section(s) added`);
      toast(msgs.join(", ") + ".", "success");
      handleCloseModal();
      fetchAssignments(selectedDesignation.id);
    } catch (err: any) {
      const msg =
        err?.response?.data?.non_field_errors?.[0] ||
        err?.response?.data?.detail ||
        "Failed to save changes.";
      toast(msg, "error");
    } finally {
      setAddLoading(false);
    }
  };

  // ── Delete Assignment ─────────────────────────────────────────────────

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleteLoading(true);
    try {
      await dispatch2(deleteData(designationSectionDetail(deleteId)));
      toast("Section removed from designation.", "success");
      setDeleteId(null);
      if (selectedDesignation) fetchAssignments(selectedDesignation.id);
    } catch {
      toast("Failed to remove section.", "error");
    } finally {
      setDeleteLoading(false);
    }
  };

  // unassigned sections for the dropdown
  const unassignedSections = sections.filter(
    (s) => !assignments.some((a) => a.section === s.section_id)
  );

  // ── Render ────────────────────────────────────────────────────────────

  return (
    <Box sx={{ width: "100%", p: 2 }}>
      {/* Designation picker */}
      <Card elevation={0} sx={{ p: 2, mb: 2, border: "1px solid", borderColor: "divider" }}>
        <Typography variant="h6" fontWeight="bold" mb={1}>
          Select Roles
        </Typography>
        <Autocomplete
          sx={{ maxWidth: 400 }}
          options={designations}
          getOptionLabel={(o) => o.designation_name}
          value={selectedDesignation}
          onChange={(_, val) => handleDesignationChange(val)}
          renderInput={(params) => (
            <TextField {...params} label="Roles" variant="standard" />
          )}
        />
      </Card>

      {/* Section assignments */}
      {selectedDesignation && (
        <Card elevation={0} sx={{ p: 2, border: "1px solid", borderColor: "divider" }}>
          {/* Title + weightage indicator */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="h6" fontWeight="bold">
              Sections for{" "}
              <span style={{ color: "#1976d2" }}>{selectedDesignation.designation_name}</span>
            </Typography>

            {/* Live weightage total badge */}
            <Chip
              icon={
                isValid ? (
                  <CheckCircleIcon fontSize="small" />
                ) : (
                  <WarningAmberIcon fontSize="small" />
                )
              }
              label={`Total: ${totalWeightage}% ${isValid ? "✓ Valid" : `(needs ${100 - totalWeightage}% more)`}`}
              color={isValid ? "success" : totalWeightage > 100 ? "error" : "warning"}
              variant="outlined"
            />
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* Loading state */}
          {loadingAssignments ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={32} />
            </Box>
          ) : assignments.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              No sections assigned yet. Click "Add / Edit Sections" to get started.
            </Typography>
          ) : (
            <Table size="small" sx={{ mb: 2 }}>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Section</strong></TableCell>
                  <TableCell align="center"><strong>Weightage (%)</strong></TableCell>
                  <TableCell align="right"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {assignments.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.section_name}</TableCell>
                    <TableCell align="center"><strong>{row.weightage}%</strong></TableCell>
                    <TableCell align="right">
                      <Tooltip title="Remove section">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setDeleteId(row.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {/* Add / Edit Sections button */}
          {!loadingAssignments && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleOpenModal}
            >
              Add / Edit Sections
            </Button>
          )}
        </Card>
      )}

      {/* ── Delete Dialog ── */}
      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)}>
        <DialogTitle fontWeight="bold">Remove Section</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove this section from{" "}
            <strong>{selectedDesignation?.designation_name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            color="error"
            disabled={deleteLoading}
            onClick={handleDelete}
            startIcon={deleteLoading ? <CircularProgress size={16} /> : null}
          >
            Remove
          </Button>
          <Button onClick={() => setDeleteId(null)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* ── Manage Sections Dialog ── */}
      <Dialog open={addSectionOpen} onClose={handleCloseModal} fullWidth maxWidth="sm">
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", pb: 1 }}>
          <Box>
            <Typography variant="h6" fontWeight="bold">Manage Sections</Typography>
            <Typography variant="body2" color="text.secondary">
              {selectedDesignation?.designation_name}
            </Typography>
          </Box>
          <IconButton size="small" onClick={handleCloseModal} sx={{ mt: 0.5 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {/* ── Existing sections with editable weightages ── */}
          {assignments.length > 0 && (
            <>
              <Typography variant="overline" fontWeight="bold" color="text.secondary">
                Existing Sections
              </Typography>
              <Typography variant="caption" display="block" color="text.secondary" mb={1}>
                Adjust weightages for sections already assigned to this designation.
              </Typography>
              <Table size="small" sx={{ mb: 2 }}>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Section</strong></TableCell>
                    <TableCell align="center"><strong>Weightage (%)</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {assignments.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>{row.section_name}</TableCell>
                      <TableCell align="center">
                        <TextField
                          size="small"
                          type="number"
                          variant="standard"
                          value={modalExistingWeightages[row.id] ?? ""}
                          onChange={(e) =>
                            setModalExistingWeightages((prev) => ({
                              ...prev,
                              [row.id]: clampTo2Dec(e.target.value),
                            }))
                          }
                          sx={{ width: 90 }}
                          inputProps={{ step: "0.01", min: "0.01", max: "100" }}
                          InputProps={{
                            endAdornment: <InputAdornment position="end">%</InputAdornment>,
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}

          {/* ── Add new sections ── */}
          {unassignedSections.length > 0 && (
            <>
              {assignments.length > 0 && <Divider sx={{ my: 2 }} />}
              <Typography variant="overline" fontWeight="bold" color="text.secondary">
                Add New Sections
              </Typography>

              {/* Remaining budget banner */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  my: 1.5,
                  p: 1.5,
                  borderRadius: 1,
                  backgroundColor: remainingBudget <= 0 ? "warning.50" : "info.50",
                  border: "1px solid",
                  borderColor: remainingBudget <= 0 ? "warning.light" : "info.light",
                }}
              >
                {remainingBudget <= 0 ? (
                  <WarningAmberIcon fontSize="small" color="warning" />
                ) : (
                  <CheckCircleIcon fontSize="small" color="info" />
                )}
                <Typography variant="body2" fontWeight="bold">
                  {remainingBudget <= 0
                    ? "All 100% is allocated in existing sections above. Reduce one to make room."
                    : `Remaining budget for new sections: ${remainingBudget}%`}
                </Typography>
              </Box>

              <Autocomplete
                multiple
                size="small"
                options={unassignedSections}
                getOptionLabel={(o) => o.section_name}
                value={newSections}
                onChange={(_, val) => {
                  const selected = val as SkillSection[];
                  setNewSections(selected);
                  setNewWeightages((prev) => {
                    const next: Record<string, string> = {};
                    selected.forEach((s) => { next[s.section_id] = prev[s.section_id] ?? ""; });
                    return next;
                  });
                }}
                disableCloseOnSelect
                renderInput={(params) => (
                  <TextField {...params} label="Select Sections to Add" variant="standard" />
                )}
                sx={{ mb: 2 }}
              />

              {newSections.length > 0 && (
                <>
                  <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
                    <Tooltip
                      title={
                        remainingBudget <= 0
                          ? "No budget remaining to split"
                          : `Auto-fill each new section with ${(remainingBudget / newSections.length).toFixed(2)}%`
                      }
                    >
                      <span>
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={remainingBudget <= 0}
                          onClick={handleSplitEqually}
                        >
                          Split Equally
                        </Button>
                      </span>
                    </Tooltip>
                  </Box>

                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Section</strong></TableCell>
                        <TableCell align="center"><strong>Weightage (%)</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {newSections.map((sec) => (
                        <TableRow key={sec.section_id}>
                          <TableCell>{sec.section_name}</TableCell>
                          <TableCell align="center">
                            <TextField
                              size="small"
                              type="number"
                              variant="standard"
                              value={newWeightages[sec.section_id] ?? ""}
                              onChange={(e) =>
                                setNewWeightages((prev) => ({
                                  ...prev,
                                  [sec.section_id]: clampTo2Dec(e.target.value),
                                }))
                              }
                              sx={{ width: 90 }}
                              inputProps={{ step: "0.01", min: "0.01", max: "100" }}
                              InputProps={{
                                endAdornment: <InputAdornment position="end">%</InputAdornment>,
                              }}
                              helperText={`Max: ${remainingBudget}%`}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </>
              )}
            </>
          )}

          {assignments.length === 0 && unassignedSections.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              All available sections are already assigned to this designation.
            </Typography>
          )}
        </DialogContent>

        <DialogActions sx={{ justifyContent: "space-between", px: 3, py: 1.5 }}>
          {/* Live total chip */}
          <Chip
            icon={modalValid ? <CheckCircleIcon fontSize="small" /> : <WarningAmberIcon fontSize="small" />}
            label={
              modalValid
                ? `Total: ${modalTotal.toFixed(2)}% ✓`
                : modalTotal > 100
                ? `Total: ${modalTotal.toFixed(2)}% — exceeds 100%`
                : `Total: ${modalTotal.toFixed(2)}% — needs ${(100 - modalTotal).toFixed(2)}% more`
            }
            color={modalValid ? "success" : modalTotal > 100 ? "error" : "warning"}
            variant="outlined"
            size="small"
          />

          <Box sx={{ display: "flex", gap: 1 }}>
            <Button onClick={handleCloseModal}>Cancel</Button>
            <Tooltip title={!modalValid ? `Total must equal 100%. Currently ${modalTotal.toFixed(2)}%.` : ""}>
              <span>
                <Button
                  variant="contained"
                  disabled={!modalValid || addLoading}
                  onClick={handleSaveAll}
                  startIcon={addLoading ? <CircularProgress size={14} /> : null}
                >
                  Save Changes
                </Button>
              </span>
            </Tooltip>
          </Box>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DesignationSectionAssignment;
